import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "./Admin.css";
import { getModuleByKey } from "./adminConfig";
import { getSchema } from "./adminFieldSchemas";
import { getModuleDefaults, MODULE_DESCRIPTIONS, normalizeHeroConfig, normalizeSiteConfig } from "./adminDefaults";
import {
  rowToFormValues,
  buildPayloadFromForm,
  emptyFormValues,
  mergeFormPayload,
  prepareExistingForMerge,
  validateForm,
} from "./adminFormUtils";
import AdminFormFields from "./AdminFormFields";
import { crudApi, siteConfigApi } from "../services/api";
import { resolveImage, getRowImage } from "../Utils/imageUtils";

const getCellValue = (row, module) => {
  if (module.key === "cars") {
    const brandRaw = row.brand?.name || row.brand;
    const brand =
      typeof brandRaw === "object" && brandRaw !== null
        ? brandRaw.name || "—"
        : brandRaw || "—";
    return `${row.location || "—"} · ${brand} · ₹${row.price ?? "—"}`;
  }
  const key = module.primary || "name";
  const val = row[key];
  if (val && typeof val === "object") return val.name || val.title || val._id;
  return val ?? row._id ?? "—";
};

const ResourceManager = () => {
  const { resourceKey } = useParams();
  const module = getModuleByKey(resourceKey);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [modal, setModal] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [singletonId, setSingletonId] = useState(null);
  const [sourceData, setSourceData] = useState({});
  const [activeSchema, setActiveSchema] = useState([]);
  const [usingFallback, setUsingFallback] = useState(false);
  const [refsLoading, setRefsLoading] = useState(false);

  const isSiteConfig = Boolean(module?.siteConfigKey);
  const isSingleton = Boolean(module?.singleton);
  const description = module ? MODULE_DESCRIPTIONS[module.key] || "" : "";

  const schema = useMemo(() => {
    if (!module) return [];
    return getSchema(module, sourceData);
  }, [module, sourceData]);

  const applyDocument = useCallback(
    (doc, fromFallback = false) => {
      const data = doc || {};
      const nextSchema = getSchema(module, data);
      setSourceData(data);
      setActiveSchema(nextSchema);
      setFormValues(emptyFormValues(nextSchema, data));
      setUsingFallback(fromFallback);
      setFormErrors({});
    },
    [module]
  );

  const load = useCallback(async () => {
    if (!module) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      if (isSiteConfig) {
        try {
          const res = await siteConfigApi.get(module.siteConfigKey);
          const doc = normalizeSiteConfig(module.siteConfigKey, res.data || {});
          setItems([doc]);
          setSingletonId(module.siteConfigKey);
          applyDocument(doc, false);
        } catch {
          const fallback = getModuleDefaults(module);
          setItems([]);
          setSingletonId(null);
          applyDocument(fallback, true);
        }
      } else {
        const res = await crudApi(module.key).list();
        const data = Array.isArray(res.data) ? res.data : [];
        setItems(data);
        if (isSingleton) {
          const first = data[0];
          setSingletonId(first?._id || null);
          const doc =
            module.key === 'hero'
              ? normalizeHeroConfig(first || getModuleDefaults(module))
              : first || getModuleDefaults(module);
          applyDocument(doc, !first);
        }
      }
    } catch (err) {
      setError(err.message || "Failed to load");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [module, isSiteConfig, isSingleton, applyDocument]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(""), 4000);
    return () => clearTimeout(t);
  }, [success]);

  const editorSchema = activeSchema.length ? activeSchema : schema;

  if (!module) {
    return <p className="admin-error">Unknown resource.</p>;
  }

  const openCreate = () => {
    const defaults = getModuleDefaults(module);
    const nextSchema = getSchema(module, defaults);
    setActiveSchema(nextSchema);
    setFormValues(emptyFormValues(nextSchema, defaults));
    setFormErrors({});
    setModal({ mode: "create" });
  };

  const openEdit = (row) => {
    const nextSchema = getSchema(module, row);
    setActiveSchema(nextSchema);
    setFormValues(rowToFormValues(row, nextSchema));
    setFormErrors({});
    setModal({ mode: "edit", id: row._id, row });
  };

  const handleSave = async (mode, id, existingRow = null) => {
    if (refsLoading) {
      setError("Please wait — dropdown options are still loading.");
      return;
    }
    const saveSchema = modal ? activeSchema : editorSchema;
    const validationErrors = validateForm(formValues, saveSchema);
    if (Object.keys(validationErrors).length) {
      setFormErrors(validationErrors);
      setError("Please fill in all required fields.");
      return;
    }

    setSaving(true);
    setError("");
    setFormErrors({});
    try {
      const built = buildPayloadFromForm(formValues, saveSchema, mode);
      const existing =
        existingRow ||
        modal?.row ||
        sourceData ||
        items.find((item) => item._id === id) ||
        {};
      const mergeBase = prepareExistingForMerge(existing, {
        siteConfig: isSiteConfig,
        preserveArrays: isSiteConfig || module.key === 'hero',
      });
      const body =
        mode === "create" && !isSiteConfig && !isSingleton
          ? built
          : mergeFormPayload(mergeBase, built);

      if (isSiteConfig) {
        const res = await siteConfigApi.update(module.siteConfigKey, body);
        const saved = normalizeSiteConfig(module.siteConfigKey, res.data || body);
        applyDocument(saved, false);
        setItems([saved]);
        setSingletonId(module.siteConfigKey);
        setSuccess("Changes saved — your updates are live on the website.");
      } else if (mode === "create") {
        await crudApi(module.key).create(body);
        setSuccess(`${module.label} created successfully.`);
        setModal(null);
      } else {
        await crudApi(module.key).update(id, body);
        if (isSingleton) {
          setSuccess("Changes saved successfully.");
        } else {
          setSuccess(`${module.label} updated successfully.`);
          setModal(null);
        }
      }
      window.dispatchEvent(new CustomEvent("carsinfinite:bootstrap-reload"));
      try {
        await load();
      } catch (loadErr) {
        console.error("Reload after save failed:", loadErr);
      }
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    setError("");
    try {
      await crudApi(module.key).remove(id);
      setSuccess("Item deleted.");
      window.dispatchEvent(new CustomEvent("carsinfinite:bootstrap-reload"));
      try {
        await load();
      } catch (loadErr) {
        console.error("Reload after save failed:", loadErr);
      }
    } catch (err) {
      setError(err.message || "Delete failed");
    }
  };

  const renderAlerts = () => (
    <>
      {usingFallback && (isSiteConfig || isSingleton) && (
        <div className="admin-banner admin-banner-info">
          Showing default content. Click <strong>Save Changes</strong> to store it in the database.
        </div>
      )}
      {error && <div className="admin-banner admin-banner-error">{error}</div>}
      {success && <div className="admin-banner admin-banner-success">{success}</div>}
    </>
  );

  const renderSingletonEditor = () => (
    <div className="admin-panel admin-singleton-editor">
      <div className="admin-panel-head">
        <div>
          <h2>{module.label}</h2>
          {description && <p className="admin-panel-desc">{description}</p>}
        </div>
        <button
          type="button"
          className="admin-btn admin-btn-primary admin-btn-auto"
          disabled={saving || refsLoading}
          onClick={() => handleSave(singletonId ? "edit" : "create", singletonId)}
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
      {renderAlerts()}
      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <div className="admin-singleton-body">
          <AdminFormFields
            schema={editorSchema}
            values={formValues}
            onChange={setFormValues}
            mode={singletonId ? "edit" : "create"}
            errors={formErrors}
            onRefsLoadingChange={setRefsLoading}
          />
        </div>
      )}
    </div>
  );

  if (isSingleton || isSiteConfig) {
    return renderSingletonEditor();
  }

  return (
    <div className="admin-panel">
      <div className="admin-panel-head">
        <div>
          <h2>{module.label}</h2>
          {description && <p className="admin-panel-desc">{description}</p>}
        </div>
        <button type="button" className="admin-btn admin-btn-primary admin-btn-auto" onClick={openCreate}>
          + Add New
        </button>
      </div>
      {renderAlerts()}
      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="admin-empty">
          <p>No {module.label.toLowerCase()} yet.</p>
          <button type="button" className="admin-btn admin-btn-primary admin-btn-auto" onClick={openCreate}>
            Create first entry
          </button>
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                {(module.imageField || module.thumbFrom) && <th>Image</th>}
                <th>Details</th>
                {module.key === "cars" && <th>Status</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const thumb = module.thumbFrom
                  ? resolveImage(row.images?.[0])
                  : getRowImage(row, module.imageField ? [module.imageField] : ["image"]);
                return (
                  <tr key={row._id}>
                    {(module.imageField || module.thumbFrom) && (
                      <td>
                        {thumb ? (
                          <img src={thumb} alt="" className="admin-thumb" />
                        ) : (
                          "—"
                        )}
                      </td>
                    )}
                    <td>{getCellValue(row, module)}</td>
                    {module.key === "cars" && (
                      <td>
                        <span className={`admin-status-pill status-${row.status}`}>
                          {row.status || "—"}
                        </span>
                      </td>
                    )}
                    <td>
                      <div className="admin-actions">
                        <button
                          type="button"
                          className="admin-btn admin-btn-ghost"
                          onClick={() => openEdit(row)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn-danger"
                          onClick={() => handleDelete(row._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="admin-modal-backdrop" onClick={() => !saving && setModal(null)}>
          <div className="admin-modal admin-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>
                {modal.mode === "create" ? "Create" : "Edit"} {module.label}
              </h3>
              <button
                type="button"
                className="admin-modal-close"
                onClick={() => !saving && setModal(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="admin-modal-body">
              {error && <div className="admin-banner admin-banner-error">{error}</div>}
              <AdminFormFields
                schema={activeSchema}
                values={formValues}
                onChange={setFormValues}
                mode={modal.mode}
                errors={formErrors}
                onRefsLoadingChange={setRefsLoading}
              />
            </div>
            <div className="admin-modal-footer">
              <button
                type="button"
                className="admin-btn admin-btn-ghost"
                disabled={saving}
                onClick={() => setModal(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-btn admin-btn-primary admin-btn-auto"
                disabled={saving || refsLoading}
                onClick={() => handleSave(modal.mode, modal.id)}
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceManager;
